import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { ChartData } from 'chart.js';

import { ICircleDataset } from '../../../types/chartsInterface';

import { reverseChartsList } from '../../../utils/chartsData';

type ChartType =
  | 'bar'
  | 'pie'
  | 'lineBar'
  | 'doughnut'
  | 'lineChart'
  | 'barStacked'
  | 'lineBarNorm'
  | 'barStackedNorm'
  | 'lineBarStacked'
  | 'lineChartStacked'
  | 'lineChartStackedNorm';

const chartComponents: Record<ChartType, any> = {
  bar: Bar,
  pie: Pie,
  lineBar: Bar,
  barStacked: Bar,
  lineChart: Line,
  lineBarNorm: Bar,
  doughnut: Doughnut,
  barStackedNorm: Bar,
  lineBarStacked: Bar,
  lineChartStacked: Line,
  lineChartStackedNorm: Line,
};

interface IChartProps {
  type: string;
  data: ChartData;
  options: any;
  isIncreasedSize: boolean;
  isHiddenItem: (string | undefined)[];
  loader: boolean;
  colorOptions: string[];
  chartType: any;
};

const ChartComponent: React.FC<IChartProps> = ({ type, data, options, isHiddenItem, isIncreasedSize, loader, colorOptions, chartType }) => {
  const ChartComponent = chartComponents[type as ChartType];
  const chartRef = useRef<any>(null);

  const { settingCharts } = useSelector((state: any) => state.charts?.chartsList);

  const shadow = settingCharts?.hasShadow || [];
  const hasShadow = shadow.includes(chartType);

  const isCircle = type === 'pie' || type === 'doughnut';

  const datasetVisibility = () => {
    const chartInstance = chartRef.current;
    if (chartInstance) {
      const { data: chartData } = chartInstance;
      chartData.datasets.forEach((dataset: ICircleDataset, index: number) => {
        // @ts-ignore
        const isHidden = isHiddenItem.includes(dataset.label) || (isHiddenItem.includes(undefined) && dataset.label === 'undefined');
        chartInstance.getDatasetMeta(index).hidden = isHidden;
      });
      chartInstance.update();
    }
  };

  useEffect(() => {
    const chartInstance = chartRef.current;
    if (chartInstance) {
      const { data: chartData } = chartInstance;
      chartData.datasets.forEach((dataset: any, index: number) => {
        const colorIndex = reverseChartsList.includes(type) ? chartData.datasets.length - (index + 1) : index;
        dataset.backgroundColor = isCircle ? colorOptions : colorOptions[colorIndex];
        const hasPositive = dataset.data.some((data: any) => data > 0);
        chartInstance.getDatasetMeta(index).hidden = !hasPositive;
      });
      chartInstance.update();
    }
    datasetVisibility();
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    const chartInstance = chartRef.current;
    if (chartInstance) {
      const { data: chartData } = chartInstance;
      chartData.datasets.forEach((dataset: any, index: number) => {
        dataset.extraInfo = hasShadow;
      });
      chartInstance.update();
    }
    // eslint-disable-next-line
  }, [hasShadow]);

  useEffect(() => {
    const chartInstance = chartRef.current;
    if (chartInstance) {
      const { data: chartData } = chartInstance;
      chartData.datasets.forEach((dataset: ICircleDataset, index: number) => {
        if (options.plugins.tooltip) {
          options.plugins.tooltip.enabled = !loader;
        }
      });
      chartInstance.update();
    }
    // eslint-disable-next-line
  }, [loader]);

  useEffect(() => {
    datasetVisibility();
    // eslint-disable-next-line
  }, [isHiddenItem]);

  return (
    <ChartComponent
      key={JSON.stringify(data)}
      ref={chartRef}
      data={data}
      options={options}
      style={{
        cursor: "pointer",
        maxWidth: isIncreasedSize ? "100vw" : isCircle ? "90vw" : "820px",
        maxHeight: isCircle ? (isIncreasedSize ? "530px" : "440px") : "60vh",
      }}
      plugins={[
        {
          id: "shadowPlugin",
          beforeDatasetsDraw: (chart: any) => {
            const { ctx } = chart;
            chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
              const meta = chart.getDatasetMeta(datasetIndex);
              if (!meta.hidden) {
                meta.data.forEach((element: any) => {
                  ctx.save();
                  if ((dataset.extraInfo === undefined && hasShadow) || dataset.extraInfo) {
                    ctx.shadowColor = "rgba(43, 43, 43, 0.5)";
                    ctx.shadowBlur = 10;
                    ctx.shadowOffsetX = 3;
                    ctx.shadowOffsetY = 3;
                  } else {
                    ctx.shadowBlur = 0;
                    ctx.shadowColor = "rgba(0, 0, 0, 0)";
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  }

                  if (chart.config.type === "bar") {
                    if (meta.indexAxis === 'y') {
                      const { x, y, width, height } = element;
                      ctx.fillStyle = '#ffffff';
                      ctx.fillRect(x - width, y - (height / 2), width, height);
                    } else {
                      const { x, y, width, height } = element;
                      ctx.fillStyle = dataset.backgroundColor;
                      ctx.fillRect(x - (width / 2), y, width, height);
                    }
                  } else if (chart.config.type === "line") {
                    if (element.hasValue()) {
                      const { x, y } = element;
                      ctx.fillStyle = '#ffffff';
                      ctx.beginPath();
                      ctx.arc(x, y, 5, 0, Math.PI * 2);
                      ctx.closePath();
                      ctx.fill();
                    }
                  } else if (isCircle) {
                    ctx.shadowBlur = 5;

                    const { x, y, startAngle, endAngle, outerRadius } = element;
                    ctx.fillStyle = '#ffffff';
                    ctx.beginPath();
                    ctx.arc(x, y, outerRadius, startAngle, endAngle);
                    ctx.closePath();
                    ctx.fill();
                  }

                  ctx.restore();
                });
              }
            });
          },
        }
      ]}
    />
  );
};

export default ChartComponent;
